/**
 * RentalsView.js
 *
 * Equipment Rentals lookup table (lt_rental) within Cost Breakdown context.
 * Uses Univer spreadsheet for a true Excel-like editing experience.
 * Cell edits auto-save to the API on blur/Enter.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import AuditLogModal from '../../common/AuditLogModal';

const COLUMN_GROUPS = [
  {
    label: 'Equipment', color: '#e8f5e9', textColor: '#2e7d32',
    columns: [
      { key: 'equipment_type', label: 'Equipment Type', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
    ]
  },
  {
    label: 'Total Hours / Instances', color: '#c8e6c9', textColor: '#1b5e20',
    columns: [
      { key: 'total_hours_structural', label: 'Total Hrs Structural', type: 'number' },
      { key: 'total_instances_structural', label: 'Total Inst. Structural', type: 'number' },
      { key: 'total_hours_mechanical', label: 'Total Hrs Mechanical', type: 'number' },
      { key: 'total_instances_mechanical', label: 'Total Inst. Mechanical', type: 'number' },
    ]
  },
  {
    label: 'Rates', color: '#fff9c4', textColor: '#f57f17',
    columns: [
      { key: 'hourly', label: 'Hourly', type: 'currency' },
      { key: 'daily', label: 'Daily', type: 'currency' },
      { key: 'weekly', label: 'Weekly', type: 'currency' },
      { key: 'monthly', label: 'Monthly', type: 'currency' },
    ]
  },
  {
    label: 'Schedule', color: '#a5d6a7', textColor: '#1b5e20',
    columns: [
      { key: 'days_wk', label: 'Days/Wk', type: 'number' },
      { key: 'hrs_day', label: 'Hrs/Day', type: 'number' },
    ]
  },
  {
    label: 'Rental Calc', color: '#e0f2f1', textColor: '#004d40',
    columns: [
      { key: 'qty', label: 'Qty', type: 'number' },
      { key: 'period_type', label: 'Period Type', type: 'text' },
      { key: 'duration', label: 'Dur.', type: 'number' },
      { key: 'basic_rental', label: 'Basic Rental', type: 'currency' },
    ]
  },
  {
    label: 'Costs', color: '#ffe0b2', textColor: '#e65100',
    columns: [
      { key: 'mob_dem', label: 'Mob/Demob', type: 'currency' },
      { key: 'fuel_factor', label: 'Fuel Fact', type: 'number' },
      { key: 'fuel_cost', label: 'Fuel Cost', type: 'currency' },
      { key: 'surcharge_factor', label: 'Surch. Fact', type: 'number' },
      { key: 'surcharge', label: 'Surcharge', type: 'currency' },
      { key: 'purchase', label: 'Purchase Cost', type: 'currency' },
      { key: 'total_cost', label: 'Total Cost', type: 'currency' },
      { key: 'site_expense', label: 'Site Expense', type: 'currency' },
    ]
  },
  {
    label: 'Details', color: '#e8eaf6', textColor: '#283593',
    columns: [
      { key: 'source', label: 'Source', type: 'text' },
      { key: 'trade', label: 'Trade', type: 'text' },
      { key: 'from_out', label: 'From Out of State?', type: 'text' },
      { key: 'subject_to', label: 'Subject to SA Tax?', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'text' },
    ]
  },
];

const ALL_COLUMNS = COLUMN_GROUPS.flatMap(g => g.columns);
const DB_COLUMNS = ALL_COLUMNS.filter(c => c.key !== 'equipment_type');
const HEADER_ROW_COUNT = 2;

const CHAR_WIDTH = 8;
const CELL_PADDING = 20;
const MIN_COL_WIDTH = 50;
const MAX_COL_WIDTH = 400;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function assignEquipmentTypes(items) {
  let currentType = '';
  return items.map(item => {
    const isCategory = item.trade === 'SubTot';
    if (isCategory) {
      currentType = item.description || '';
      return { ...item, equipment_type: currentType };
    }
    return { ...item, equipment_type: currentType };
  });
}

function calcAutoFitWidth(label, items, colKey, colType) {
  let maxLen = label.length;
  for (const item of items) {
    const val = item[colKey];
    if (val != null && val !== '') {
      const strLen = colType === 'currency'
        ? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}`.length
        : String(val).length;
      if (strLen > maxLen) maxLen = strLen;
    }
  }
  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, maxLen * CHAR_WIDTH + CELL_PADDING));
}

function buildWorkbookData(items) {
  const enrichedItems = assignEquipmentTypes(items);
  const colCount = ALL_COLUMNS.length + 1; // +1 for hidden id column (col 0)
  const rowCount = enrichedItems.length + HEADER_ROW_COUNT;
  const cellData = {};
  const mergeData = [];

  // Row 0: group header row
  cellData[0] = {};
  cellData[0][0] = { v: 'ID', s: { bg: { rgb: '#f9fafb' }, ft: { b: 1 }, cl: { rgb: '#6b7280' }, ht: 2 } };

  let colOffset = 1;
  COLUMN_GROUPS.forEach(group => {
    const rgb = hexToRgb(group.color);
    const textRgb = hexToRgb(group.textColor);
    const startCol = colOffset;
    const endCol = colOffset + group.columns.length - 1;

    cellData[0][startCol] = {
      v: group.label,
      s: {
        bg: { rgb: `rgb(${rgb.r},${rgb.g},${rgb.b})` },
        cl: { rgb: `rgb(${textRgb.r},${textRgb.g},${textRgb.b})` },
        ft: { b: 1 },
        ht: 2,
        fs: 11,
      }
    };

    if (group.columns.length > 1) {
      mergeData.push({
        startRow: 0, endRow: 0,
        startColumn: startCol, endColumn: endCol,
      });
    }

    colOffset += group.columns.length;
  });

  // Row 1: column header row
  cellData[1] = {};
  cellData[1][0] = { v: 'ID', s: { bg: { rgb: '#f9fafb' }, ft: { b: 1 }, cl: { rgb: '#6b7280' }, ht: 2, fs: 10 } };
  ALL_COLUMNS.forEach((col, i) => {
    const group = COLUMN_GROUPS.find(g => g.columns.some(c => c.key === col.key));
    const rgb = group ? hexToRgb(group.color) : { r: 249, g: 250, b: 251 };
    cellData[1][i + 1] = {
      v: col.label,
      s: {
        bg: { rgb: `rgb(${rgb.r},${rgb.g},${rgb.b})` },
        cl: { rgb: '#6b7280' },
        ft: { b: 1 },
        ht: 2,
        fs: 10,
      }
    };
  });

  // Row 2+: data rows
  enrichedItems.forEach((item, rowIdx) => {
    const row = HEADER_ROW_COUNT + rowIdx;
    const isCategory = item.trade === 'SubTot';
    cellData[row] = {};
    cellData[row][0] = { v: item.id, t: 2 };

    ALL_COLUMNS.forEach((col, colIdx) => {
      const val = item[col.key];
      const cellVal = {};

      if (val != null && val !== '') {
        if ((col.type === 'number' || col.type === 'currency') && col.key !== 'equipment_type') {
          cellVal.v = parseFloat(val);
          cellVal.t = 2;
        } else {
          cellVal.v = String(val);
        }
      } else {
        cellVal.v = '';
      }

      if (isCategory) {
        cellVal.s = {
          bg: { rgb: 'rgb(224,242,255)' },
          ft: { b: 1 },
          cl: { rgb: '#1565c0' },
        };
      }

      cellData[row][colIdx + 1] = cellVal;
    });
  });

  // Column widths: col 0 hidden, rest auto-fitted
  const columnData = { 0: { w: 0, hd: 1 } };
  ALL_COLUMNS.forEach((col, i) => {
    const width = calcAutoFitWidth(col.label, enrichedItems, col.key, col.type);
    columnData[i + 1] = { w: width };
  });

  return {
    sheetOrder: ['rentals'],
    sheets: {
      rentals: {
        id: 'rentals',
        name: 'Equipment Rentals',
        rowCount,
        columnCount: colCount,
        cellData,
        columnData,
        mergeData,
        defaultRowHeight: 24,
        defaultColumnWidth: 100,
        freeze: { startRow: HEADER_ROW_COUNT, startColumn: 0, xSplit: 0, ySplit: HEADER_ROW_COUNT },
      },
    },
  };
}

const RentalsView = ({ estimate = null }) => {
  const { instance, accounts } = useMsal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}));
  const [recordCount, setRecordCount] = useState({ total: 0, filtered: 0 });
  const containerRef = useRef(null);
  const univerRef = useRef(null);
  const itemsRef = useRef([]);
  const savingRef = useRef(false);
  const fetchItemsRef = useRef(null);
  const socketIdRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handlePullToEstimate = useCallback(async () => {
    if (!estimate?.id) {
      showToast('Select an estimate first', 'error');
      return;
    }
    if (!window.confirm(
      `Pull Rentals library into estimate ${estimate.estimate_number}?\n\n`
      + '• Outside Rentals rows per matched discipline (account RENTAL)\n'
      + '• Site expense lines from rental site_expense\n'
      + '• Unmapped trades → Indirects\n\n'
      + 'Prior RENTAL-tagged pulls for this estimate are replaced.'
    )) return;
    setPulling(true);
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'POST', '/cost-breakdown/rentals/pull-to-estimate',
        { estimate_id: estimate.id }
      );
      showToast(res.data?.message || 'Rentals pulled');
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setPulling(false);
    }
  }, [estimate, instance, accounts, showToast]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/rentals');
      if (result.data?.success) {
        const data = result.data.data || [];
        setItems(data);
        itemsRef.current = data;
      }
    } catch (err) {
      console.error('Error fetching rentals:', err);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  const handleDataChange = useCallback((change) => {
    const api = univerRef.current;
    if (!api) return;

    if (change.action === 'cell-updated' && change.rowId && change.fields) {
      const dataIdx = itemsRef.current.findIndex(it => it.id === change.rowId);
      if (dataIdx === -1) return;

      const sheet = api.getActiveWorkbook()?.getActiveSheet();
      if (!sheet) return;

      const dataRow = HEADER_ROW_COUNT + dataIdx;
      Object.entries(change.fields).forEach(([fieldKey, fieldValue]) => {
        if (fieldKey === 'id' || fieldKey === 'created_at' || fieldKey === 'updated_at') return;
        const colIdx = ALL_COLUMNS.findIndex(c => c.key === fieldKey);
        if (colIdx === -1) return;
        const univCol = colIdx + 1;

        const colDef = ALL_COLUMNS[colIdx];
        let cellValue = fieldValue;
        if ((colDef.type === 'number' || colDef.type === 'currency') && cellValue != null && cellValue !== '') {
          cellValue = parseFloat(cellValue);
        }

        try {
          const range = sheet.getRange(dataRow, univCol);
          range.setValue(cellValue ?? '');
        } catch (e) {
          console.warn('Live update cell error:', e);
        }

        itemsRef.current[dataIdx] = { ...itemsRef.current[dataIdx], [fieldKey]: fieldValue };
      });
    } else if (change.action === 'row-added' || change.action === 'row-deleted' || change.action === 'bulk-refresh') {
      if (fetchItemsRef.current) fetchItemsRef.current();
    }
  }, []);

  const { viewers, isConnected, connectionState, socketId } = usePresence('rentals', 'rentals-view', true, handleDataChange);

  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    if (loading || !containerRef.current) return;
    let disposed = false;

    const initUniver = async () => {
      try {
        const { createUniver, LocaleType, mergeLocales } = await import('@univerjs/presets');
        const { UniverSheetsCorePreset } = await import('@univerjs/preset-sheets-core');
        const UniverPresetSheetsCoreEnUS = (await import('@univerjs/preset-sheets-core/locales/en-US')).default;
        await import('@univerjs/preset-sheets-core/lib/index.css');

        const { UniverSheetsFilterPreset } = await import('@univerjs/preset-sheets-filter');
        const UniverPresetSheetsFilterEnUS = (await import('@univerjs/preset-sheets-filter/locales/en-US')).default;
        await import('@univerjs/preset-sheets-filter/lib/index.css');

        const { UniverSheetsSortPreset } = await import('@univerjs/preset-sheets-sort');
        const UniverPresetSheetsSortEnUS = (await import('@univerjs/preset-sheets-sort/locales/en-US')).default;
        await import('@univerjs/preset-sheets-sort/lib/index.css');

        const { UniverSheetsFindReplacePreset } = await import('@univerjs/preset-sheets-find-replace');
        const UniverPresetSheetsFindReplaceEnUS = (await import('@univerjs/preset-sheets-find-replace/locales/en-US')).default;
        await import('@univerjs/preset-sheets-find-replace/lib/index.css');

        const { UniverSheetsConditionalFormattingPreset } = await import('@univerjs/preset-sheets-conditional-formatting');
        const UniverPresetSheetsConditionalFormattingEnUS = (await import('@univerjs/preset-sheets-conditional-formatting/locales/en-US')).default;
        await import('@univerjs/preset-sheets-conditional-formatting/lib/index.css');

        const { UniverSheetsDataValidationPreset } = await import('@univerjs/preset-sheets-data-validation');
        const UniverPresetSheetsDataValidationEnUS = (await import('@univerjs/preset-sheets-data-validation/locales/en-US')).default;
        await import('@univerjs/preset-sheets-data-validation/lib/index.css');

        const { UniverSheetsHyperLinkPreset } = await import('@univerjs/preset-sheets-hyper-link');
        const UniverPresetSheetsHyperLinkEnUS = (await import('@univerjs/preset-sheets-hyper-link/locales/en-US')).default;
        await import('@univerjs/preset-sheets-hyper-link/lib/index.css');

        const { UniverSheetsThreadCommentPreset } = await import('@univerjs/preset-sheets-thread-comment');
        const UniverPresetSheetsThreadCommentEnUS = (await import('@univerjs/preset-sheets-thread-comment/locales/en-US')).default;
        await import('@univerjs/preset-sheets-thread-comment/lib/index.css');

        if (disposed || !containerRef.current) return;

        if (univerRef.current) {
          try { univerRef.current.dispose(); } catch (e) { /* ignore */ }
          univerRef.current = null;
        }

        const workbookData = buildWorkbookData(items);

        const { univerAPI } = createUniver({
          locale: LocaleType.EN_US,
          locales: {
            [LocaleType.EN_US]: mergeLocales(
              UniverPresetSheetsCoreEnUS,
              UniverPresetSheetsFilterEnUS,
              UniverPresetSheetsSortEnUS,
              UniverPresetSheetsFindReplaceEnUS,
              UniverPresetSheetsConditionalFormattingEnUS,
              UniverPresetSheetsDataValidationEnUS,
              UniverPresetSheetsHyperLinkEnUS,
              UniverPresetSheetsThreadCommentEnUS,
            ),
          },
          presets: [
            UniverSheetsCorePreset({
              container: containerRef.current,
            }),
            UniverSheetsFilterPreset(),
            UniverSheetsSortPreset(),
            UniverSheetsFindReplacePreset(),
            UniverSheetsConditionalFormattingPreset(),
            UniverSheetsDataValidationPreset(),
            UniverSheetsHyperLinkPreset(),
            UniverSheetsThreadCommentPreset(),
          ],
        });

        univerAPI.createWorkbook(workbookData);
        univerRef.current = univerAPI;

        // Create auto-filter on the data range (header row 1 through last data row, all columns)
        try {
          const sheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
          if (sheet && items.length > 0) {
            const lastRow = HEADER_ROW_COUNT + items.length;
            const lastCol = ALL_COLUMNS.length; // col 0 is hidden ID, data cols are 1..lastCol
            const filterRange = sheet.getRange(1, 1, lastRow - 1, lastCol);
            filterRange.createFilter();
          }
        } catch (e) {
          console.warn('Auto-filter setup:', e);
        }

        // Track recently saved cells to avoid duplicate saves from SheetEditEnded + SheetValueChanged
        const recentlySaved = new Map();
        const saveCell = (row, column) => {
          if (savingRef.current) return;
          if (row < HEADER_ROW_COUNT || column === 0) return;

          const cellKey = `${row}:${column}`;
          const now = Date.now();
          if (recentlySaved.has(cellKey) && now - recentlySaved.get(cellKey) < 500) return;
          recentlySaved.set(cellKey, now);

          const colDef = ALL_COLUMNS[column - 1];
          if (!colDef) return;
          if (colDef.key === 'equipment_type') return;

          const sheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
          if (!sheet) return;

          const id = sheet.getRange(row, 0)?.getValue();
          if (!id) return;

          let newValue = sheet.getRange(row, column)?.getValue();

          if ((colDef.type === 'number' || colDef.type === 'currency') && newValue != null && newValue !== '') {
            newValue = parseFloat(newValue);
            if (isNaN(newValue)) newValue = null;
          }
          if (newValue === '' || newValue === undefined) newValue = null;

          savingRef.current = true;
          const headers = socketIdRef.current ? { 'x-socket-id': socketIdRef.current } : {};
          authenticatedApiCall(instance, accounts, 'PUT', `/cost-breakdown/rentals/${id}`, { [colDef.key]: newValue }, { headers })
            .then(() => {
              const idx = itemsRef.current.findIndex(it => it.id === id);
              if (idx !== -1) {
                itemsRef.current[idx] = { ...itemsRef.current[idx], [colDef.key]: newValue };
              }
            })
            .catch(() => {
              showToast('Failed to save cell', 'error');
            })
            .finally(() => {
              savingRef.current = false;
            });
        };

        // Auto-save on cell edit (typing + Enter/blur)
        univerAPI.addEvent(univerAPI.Event.SheetEditEnded, (params) => {
          saveCell(params.row, params.column);
        });

        // Auto-save on cell clear/delete/paste/undo (catches Delete key, Ctrl+X, etc.)
        univerAPI.addEvent(univerAPI.Event.SheetValueChanged, (params) => {
          if (!params.effectedRanges || params.effectedRanges.length === 0) return;
          for (const range of params.effectedRanges) {
            const startRow = range.getRow();
            const startCol = range.getColumn();
            const numRows = range.getHeight();
            const numCols = range.getWidth();
            for (let r = startRow; r < startRow + numRows; r++) {
              for (let c = startCol; c < startCol + numCols; c++) {
                saveCell(r, c);
              }
            }
          }
        });

        // Prevent editing header rows, id column, and equipment_type column
        univerAPI.addEvent(univerAPI.Event.BeforeSheetEditStart, (params) => {
          const { row, column } = params;
          if (row < HEADER_ROW_COUNT || column === 0) {
            params.cancel = true;
            return;
          }
          const colDef = ALL_COLUMNS[column - 1];
          if (colDef && colDef.key === 'equipment_type') {
            params.cancel = true;
          }
        });

        setRecordCount({ total: items.length, filtered: items.length });
      } catch (err) {
        console.error('Failed to initialize Univer:', err);
        showToast('Failed to initialize spreadsheet', 'error');
      }
    };

    initUniver();

    return () => {
      disposed = true;
      if (univerRef.current) {
        try { univerRef.current.dispose(); } catch (e) { /* ignore */ }
        univerRef.current = null;
      }
    };
  }, [loading, items, instance, accounts, showToast]);

  // Search filtering via row visibility
  useEffect(() => {
    const api = univerRef.current;
    if (!api || !items.length) return;

    try {
      const workbook = api.getActiveWorkbook();
      if (!workbook) return;
      const sheet = workbook.getActiveSheet();
      if (!sheet) return;

      let filteredCount = 0;
      const s = search.toLowerCase();

      items.forEach((item, idx) => {
        const dataRow = HEADER_ROW_COUNT + idx;
        const matches = !s || ALL_COLUMNS.some(col =>
          String(item[col.key] ?? '').toLowerCase().includes(s)
        );

        if (matches) {
          filteredCount++;
          try {
            const range = sheet.getRange(`${dataRow + 1}:${dataRow + 1}`);
            sheet.showRows(range);
          } catch (e) { /* already visible */ }
        } else {
          try {
            const range = sheet.getRange(`${dataRow + 1}:${dataRow + 1}`);
            sheet.hideRow(range);
          } catch (e) { /* already hidden */ }
        }
      });

      setRecordCount({ total: items.length, filtered: s ? filteredCount : items.length });
    } catch (err) {
      console.error('Search filter error:', err);
    }
  }, [search, items]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const handleAdd = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/rentals', {}, syncHeaders());
      fetchItems();
      showToast('Row added');
    } catch (err) {
      showToast('Failed to add', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedRows.size} selected rows?`)) return;
    try {
      for (const id of selectedRows) {
        await authenticatedApiCall(instance, accounts, 'DELETE', `/cost-breakdown/rentals/${id}`, null, syncHeaders());
      }
      setSelectedRows(new Set());
      fetchItems();
      showToast(`Deleted ${selectedRows.size} rows`);
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  // Column visibility
  useEffect(() => {
    const api = univerRef.current;
    if (!api) return;

    try {
      const workbook = api.getActiveWorkbook();
      if (!workbook) return;
      const sheet = workbook.getActiveSheet();
      if (!sheet) return;

      ALL_COLUMNS.forEach((col, idx) => {
        const univColIdx = idx + 1;
        try {
          const colLetter = String.fromCharCode(65 + univColIdx);
          const range = sheet.getRange(`${colLetter}:${colLetter}`);
          if (visibleColumns[col.key]) {
            sheet.showColumns(range);
          } else {
            sheet.hideColumn(range);
          }
        } catch (e) { /* skip */ }
      });
    } catch (err) {
      console.error('Column visibility error:', err);
    }
  }, [visibleColumns]);

  return (
    <div style={{ padding: '16px 24px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, padding: '12px 20px', borderRadius: '8px', color: 'white', fontWeight: '500', backgroundColor: toast.type === 'error' ? '#dc3545' : '#198754', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem', flexShrink: 0 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1f2937' }}>Equipment Rentals; Local to Project Site</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            {recordCount.filtered} record{recordCount.filtered !== 1 ? 's' : ''}{search ? ` of ${recordCount.total} (filtered)` : ''}
            {' '}<span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Click any cell to edit</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setShowColumnConfig(!showColumnConfig)} style={toolbarBtnStyle} title="Column visibility">Columns</button>
          <button onClick={handleAdd} style={{ ...toolbarBtnStyle, backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545' }}>+ Add Row</button>
          {selectedRows.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...toolbarBtnStyle, backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545' }}>
              Delete ({selectedRows.size})
            </button>
          )}
          {estimate?.id && (
            <button
              onClick={handlePullToEstimate}
              disabled={pulling}
              style={{ ...toolbarBtnStyle, backgroundColor: '#0f766e', color: 'white', border: '1px solid #0f766e' }}
              title="Allocate this library into the active estimate (Outside Rentals / Site / Indirects)"
            >
              {pulling ? '⏳ Pulling…' : '⬆️ Pull into Estimate'}
            </button>
          )}
          <button onClick={() => fetchItems()} style={toolbarBtnStyle} title="Refresh data">Refresh</button>
          <button onClick={() => setShowAudit(true)} style={toolbarBtnStyle} title="Audit log">📜 Audit Log</button>
          <button onClick={() => setShowHelpModal(true)} style={toolbarBtnStyle} title="Help & info">Help</button>
        </div>
      </div>
      <AuditLogModal
        isOpen={showAudit}
        onClose={() => setShowAudit(false)}
        endpoint="/cost-breakdown/audit-log?table_name=lt_rental"
        title="Rentals Audit Log"
        accentColor="#198754"
      />

      {/* Search */}
      <div style={{ backgroundColor: '#f8f9fa', padding: '0.5rem 1rem', borderRadius: '6px', marginBottom: '0.75rem', display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Search</label>
          <input type="text" placeholder="Search all columns..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '280px', outline: 'none' }}
          />
        </div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', alignSelf: 'flex-end', paddingBottom: '4px' }}>
          {recordCount.filtered} of {recordCount.total} records
        </div>
      </div>

      {/* Column Config Modal */}
      {showColumnConfig && (
        <div style={modalOverlayStyle} onClick={() => setShowColumnConfig(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Column Configuration</h3>
              <button onClick={() => setShowColumnConfig(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>x</button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button onClick={() => setVisibleColumns(ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}))} style={smallBtnStyle}>Show All</button>
              <button onClick={() => setVisibleColumns(ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: false }), {}))} style={smallBtnStyle}>Hide All</button>
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {COLUMN_GROUPS.map(group => (
                <div key={group.label} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    fontSize: '0.85rem', fontWeight: '600', color: group.textColor,
                    backgroundColor: group.color, padding: '6px 10px', borderRadius: '4px', marginBottom: '0.5rem',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span>{group.label}</span>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button onClick={() => {
                        const next = { ...visibleColumns };
                        group.columns.forEach(c => { next[c.key] = true; });
                        setVisibleColumns(next);
                      }} style={{ ...smallBtnStyle, padding: '2px 8px', fontSize: '0.75rem' }}>All</button>
                      <button onClick={() => {
                        const next = { ...visibleColumns };
                        group.columns.forEach(c => { next[c.key] = false; });
                        setVisibleColumns(next);
                      }} style={{ ...smallBtnStyle, padding: '2px 8px', fontSize: '0.75rem' }}>None</button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem', paddingLeft: '0.5rem' }}>
                    {group.columns.map(col => (
                      <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={visibleColumns[col.key]} onChange={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))} />
                        {col.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setShowColumnConfig(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && ReactDOM.createPortal(
        <div style={modalOverlayStyle} onClick={() => setShowHelpModal(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '700px', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Equipment Rentals - Help</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="rentals-help" title="Equipment Rentals Help" accent="#198754" />
                <button onClick={() => setShowHelpModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>x</button>
              </div>
            </div>

            <HelpWithVideoTour guideId="rentals-help" title="Equipment Rentals Help" accent="#198754">
            <div style={{ backgroundColor: '#dc3545', color: 'white', padding: '10px 14px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '600', lineHeight: 1.5 }}>
              IMPORTANT NOTE: For any Rentals subject to S.A. Taxes and/or Duties; the value to be listed in the "Total Rate" field(s) of the Rental Section of each applicable Discipline. Other Note: For Shops Rentals; Currency to be noted in "Comments".
            </div>

            <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.7 }}>
              <h4 style={{ margin: '0 0 8px', color: '#1f2937' }}>How to Use</h4>
              <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>
                <li><strong>Click</strong> any cell to edit it directly (auto-saves on Enter or click away)</li>
                <li><strong>Equipment Type</strong> column (read-only) shows the category each item belongs to</li>
                <li><strong>Blue highlighted rows</strong> are category subtotal headers (CRANES, MAN LIFTS, etc.)</li>
                <li>Use <strong>Ctrl+C / Ctrl+V</strong> to copy/paste between cells (works with Excel)</li>
                <li>Use <strong>Ctrl+Z</strong> to undo changes within the session</li>
                <li><strong>Drag column borders</strong> to resize columns</li>
              </ul>

              <h4 style={{ margin: '0 0 8px', color: '#1f2937' }}>Toolbar</h4>
              <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>
                <li><strong>Columns</strong> - Show/hide columns by group</li>
                <li><strong>+ Add Row</strong> - Add a new empty row at the end</li>
                <li><strong>Refresh</strong> - Reload data from the database</li>
                <li><strong>Search</strong> - Filter rows across all columns</li>
              </ul>

              <h4 style={{ margin: '0 0 8px', color: '#1f2937' }}>Column Groups</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '16px' }}>
                {COLUMN_GROUPS.map(g => (
                  <div key={g.label} style={{ padding: '4px 10px', backgroundColor: g.color, color: g.textColor, borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600 }}>
                    {g.label} ({g.columns.length} cols)
                  </div>
                ))}
              </div>

              <h4 style={{ margin: '0 0 8px', color: '#1f2937' }}>Equipment Categories</h4>
              <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#6b7280' }}>
                31 categories including: Cranes, Lifting & Rigging, Man Lifts, Forklifts, Excavation, Pumps, Transportation, Trailers, Air Compressor, Generator / Light Tower, Tool Rooms / Office Trailers, Welders, Torches, Paint Equipment, Concrete, Survey, Ironworkers, Pipe tools, Cleaning / Heating, Sheet Metal, Confined Space, Fuel, Communications, Fall Protection, Millwright, and Others.
              </p>
            </div>
            </HelpWithVideoTour>

            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setShowHelpModal(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Univer Spreadsheet */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
          Loading equipment rentals...
        </div>
      ) : (
        <div ref={containerRef} style={{
          flex: 1, minHeight: 0, borderRadius: '8px', overflow: 'hidden',
          border: '1px solid #e5e7eb', backgroundColor: 'white',
        }} />
      )}
    </div>
  );
};

const toolbarBtnStyle = {
  padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  display: 'flex', alignItems: 'center', gap: '6px'
};

const smallBtnStyle = {
  padding: '4px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
  borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem'
};

const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '500px',
  width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

export default RentalsView;
